import React, {
    Fragment,
    useCallback, useContext, useEffect,
    useRef, useState,
} from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import IconButton from '@material-ui/core/IconButton';
import CloseIcon from '@material-ui/icons/Close';
import clsx from 'clsx';
import { includes, isEmpty } from 'lodash';

import useStyle from './EditEmailTemplatePageStyles';
import EmailSelectComponent from '../email-editor/EmailSelectComponent';
import EmailSubjectAndBodyInputComponent from '../email-editor/EmailSubjectAndBodyInputComponent';

import Button from '../../material-ui-kit/CustomButtons/Button';
import LoadingContext, { LOADING_AREAS } from '../../../contexts/LoadingContext';

// Modified by AI on 05/26/2026. Edit #1.
const EditEmailTemplatesComponent = ({
    className = '',
    emailTemplates = [],
    selectedTemplate = null,
    onSelectedTemplateChange = () => {},
    formData = {
        subject: '',
        body: '',
        description: '',
    },
    invalidFieldNames = [],
    onEmailDataChange = () => {},
    onClose = () => {},
    isValidForm = true,
    appEventForExternalSubjectChange = null,
    appEventForExternalBodyChange = null,
    onDescriptionChange = () => {},
    onSaveEmailTemplate = () => {},
    sendTestMessage = () => {},
    textTemplateValues,
    buttonTemplateValues,
}) => {
    const containerRef = useRef(null);
    const {
        isLoading,
    } = useContext(LoadingContext);

    const { t } = useTranslation();
    const classes = useStyle();
    const jobDescriptionRef = useRef();

    const isDataFetching = isLoading(LOADING_AREAS.metadataEmailTemplatesTemplates);
    const isEmailSending = isLoading(LOADING_AREAS.metadataEmailTemplatesTemplatesTestMessage);

    const [isDescriptionFocused, setIsDescriptionFocused] = useState(false);

    const rootClasses = clsx(className, classes.root);

    const onEmailSubjectChange = useCallback((subject) => {
        onEmailDataChange((prevState) => ({ ...prevState, subject }));
    }, []);

    const onEmailBodyChange = useCallback((body) => {
        onEmailDataChange((prevState) => ({ ...prevState, body }));
    }, []);

    const onJobDescriptionFocusIn = useCallback(() => {
        setIsDescriptionFocused(true);
    }, []);

    const onJobDescriptionFocusOut = useCallback(() => {
        setIsDescriptionFocused(false);
    }, []);

    useEffect(() => {
        jobDescriptionRef?.current?.focus();
        setIsDescriptionFocused(true);
    }, [selectedTemplate, jobDescriptionRef]);

    return (
        <Fragment>
            <div className={classes.pageContainer} ref={containerRef}>
                <div className={classes.headerContainer}>
                    {t('CONTACT_CANDIDATE_EMAIL_FORM_HEADER_LABEL')}
                    <div className={classes.closeButtonContainer}>
                        <IconButton
                            className={classes.closeButton}
                            aria-label='close'
                            onClick={onClose}>
                            <CloseIcon/>
                        </IconButton>
                    </div>
                </div>
                <div className={classes.selectTemplateContainer}>
                    <div className={classes.selectTemplateLabel}>
                        {t('CONTACT_CANDIDATE_EMAIL_FORM_SELECT_TEMPLATE')}
                    </div>
                    <EmailSelectComponent
                        className={classes.selectTemplateSelect}
                        selectedTemplate={selectedTemplate}
                        emailTemplates={emailTemplates}
                        onSelectedEmailTemplateChange={onSelectedTemplateChange}
                    />
                </div>
                <div className={classes.textAreaContainer}>
                    <textarea
                        ref={jobDescriptionRef}
                        onFocus={onJobDescriptionFocusIn}
                        onBlur={onJobDescriptionFocusOut}
                        className={classes.textArea}
                        placeholder={'Description'}
                        rows="2"
                        dir="auto"
                        onChange={(ev) => {
                            onDescriptionChange(ev.target.value);
                        }}
                        value={formData.description}
                    />
                </div>

                <div className={classes.textEditorContainer}>
                    <EmailSubjectAndBodyInputComponent
                        className={classes.emailSubjectAndBodyInput}
                        disableLink={false}
                        emailSubject={formData.subject}
                        emailSubjectError={includes(invalidFieldNames, 'subject')}
                        emailBody={formData.body}
                        emailBodyError={includes(invalidFieldNames, 'body')}
                        onEmailSubjectChange={onEmailSubjectChange}
                        onEmailBodyChange={onEmailBodyChange}
                        appEventForExternalSubjectChange={appEventForExternalSubjectChange}
                        appEventForExternalBodyChange={appEventForExternalBodyChange}
                        textTemplateValues={textTemplateValues}
                        buttonTemplateValues={buttonTemplateValues}
                        isFeedbackActionShown={!isEmpty(buttonTemplateValues)}
                        isTemplateTextShown={!isDescriptionFocused}
                    />
                </div>
                <div className={classes.actionsContainer}>
                    <Button
                        testid={'email-template-save-btn'}
                        color='primary'
                        variant='outlined'
                        className={classes.emailContactBtn}
                        onClick={onSaveEmailTemplate}
                        disabled={!isValidForm || isDataFetching}
                    >
                        {t('SAVE_METADATA_EMAIL_TEMPLATE_BUTTON', 'Save')}
                    </Button>
                    <Button
                        testid={'email-template-save-btn'}
                        color='primary'
                        variant='outlined'
                        className={classes.emailContactBtn}
                        disabled={!isValidForm || isEmailSending || isDataFetching}
                        spin={isEmailSending}
                        onClick={sendTestMessage}
                    >
                        {t('SEND_EMAIL_TEMPLATE_TEST_MESSAGE_BUTTON', 'Test Message')}
                    </Button>
                </div>
            </div>
        </Fragment>
    );
};

EditEmailTemplatesComponent.propTypes = {
    className: PropTypes.string,
    emailTemplates: PropTypes.array,
    invalidFieldNames: PropTypes.array,
    selectedTemplate: PropTypes.object,
    formData: PropTypes.object,
    emailContextData: PropTypes.object,
    onEmailDataChange: PropTypes.func,
    sendTestMessage: PropTypes.func,
    onSelectedTemplateChange: PropTypes.func,
    onSaveEmailTemplate: PropTypes.func,
    onDescriptionChange: PropTypes.func,
    onClose: PropTypes.func,
    isValidForm: PropTypes.bool,
    appEventForExternalSubjectChange: PropTypes.string,
    appEventForExternalBodyChange: PropTypes.string,
};

export default EditEmailTemplatesComponent;
